// The ladder and the Climb the ladder rules, in one place so the API (which records results) and the app agree.
// The adult picks the unit a learner starts on. The learner practises that unit's worksheets until they are ready,
// then takes the unit test: passing it moves them to the next unit; otherwise they keep practising and retake it
// whenever the adult decides. No timing, no days.
import ladderData from './ladder.json';

export interface StepInfo { step: number; title: string; hint: string }
/** optional: the ladder steps over it; a learner only does it when moved there by hand. test: the unit test's size. */
export interface StageInfo { stage: number; title: string; grades: string; optional?: boolean; steps: StepInfo[]; test: { total: number; passMark: number } }
export const LADDER = ladderData as StageInfo[];
export const STAGE_COUNT = LADDER.length;

export function stageInfo(stage: number): StageInfo {
  const s = LADDER[stage - 1];
  if (!s) throw new Error(`no stage ${stage}`);
  return s;
}

const gradeNum = (t: string) => (t === 'PreK' ? -1 : t === 'K' ? 0 : Number(t));
const gradeWord = (g: number) => (g === -1 ? 'Pre-K' : g === 0 ? 'K' : String(g));

/** The grades a set of stages is typically used in, from their grade bands: "Pre-K to Grade 2", "Grades 9 to 11", "Grade 12". */
export function gradeRange(stages: number[]): string {
  const nums = stages.flatMap((n) => (stageInfo(n).grades.match(/PreK|K|\d+/g) ?? []).map(gradeNum));
  if (!nums.length) return '';
  const lo = Math.min(...nums), hi = Math.max(...nums);
  if (lo === hi) return lo > 0 ? `Grade ${lo}` : gradeWord(lo);
  if (lo > 0) return `Grades ${lo} to ${hi}`;
  return `${gradeWord(lo)} to ${hi > 0 ? `Grade ${hi}` : gradeWord(hi)}`;
}

/** The next stage on the ladder, stepping over optional stages; null after the last. */
export function stageAfter(stage: number): number | null {
  for (let n = stage + 1; n <= LADDER.length; n++) if (!stageInfo(n).optional) return n;
  return null;
}

/** The stage before on the ladder, stepping over optional stages; null before the first. */
export function stageBefore(stage: number): number | null {
  for (let n = stage - 1; n >= 1; n--) if (!stageInfo(n).optional) return n;
  return null;
}

// ---------------------------------------------------------------- unit tests
/** The pass mark for a test of n scored problems: 95%, with one mistake allowed once there are 8 or more
 *  (build.passmark in the generator prints the same number on the test). */
export function passMark(n: number): number {
  const m = Math.ceil(0.95 * n - 1e-9);
  return n >= 8 ? Math.min(m, n - 1) : m;
}

export type AttemptKind = 'placement' | 'unit';
/** passed / not_yet: a unit test. placed (a placement test) and the others are from earlier versions (history only). */
export type Outcome = 'passed' | 'not_yet' | 'placed' | 'close' | 'stuck' | 'stage_complete';

export const OUTCOME_LABEL: Record<Outcome, string> = {
  passed: 'Passed', not_yet: 'Not yet', placed: 'Placed', close: 'Close', stuck: 'Stuck', stage_complete: 'Unit complete',
};

/** A unit test result: passed moves the learner to the next unit (or finishes the ladder). */
export function decideUnitTest(stage: number, correct: number, total: number) {
  const pass = passMark(total);
  const passed = correct >= pass;
  const after = stageAfter(stage);
  return { outcome: (passed ? 'passed' : 'not_yet') as Outcome, passMark: pass, next: passed ? after : stage, done: passed && !after };
}
